/**
 * User Management Service - API-based user CRUD operations
 */

import { auth } from '../sp-firebase/firebase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export interface UserData {
  id: string;
  email: string;
  name: string | null;
  isSuperadmin: boolean;
  isOwner?: boolean;
  accountId: string | null;
  accountName: string | null;
  role: string | null;
  status: string;
  ssoExempt?: boolean;
  firebaseUid?: string;
  lastLogin: string | null;
  loginCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AccountData {
  id: string;
  name: string;
  country?: string;
  timezone?: string;
  currency?: string;
  demandTerm?: string;
  supplyTerm?: string;
  hedgeTerm?: string;
  viewingHorizonMonths?: number;
  createdAt: string;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  isSuperadmin?: boolean;
  accountId?: string;
  role?: string;
}

export interface UpdateUserRequest {
  name?: string;
  isSuperadmin?: boolean;
  accountId?: string;
  role?: string;
  status?: string;
  ssoExempt?: boolean;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

/**
 * Get all users (superadmin only)
 */
export async function getAllUsers(): Promise<UserData[]> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/api/v2/users`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch users');
  }

  const data = await response.json();
  return data.users;
}

/**
 * Get user by ID (superadmin only)
 */
export async function getUserById(userId: string): Promise<UserData> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/api/v2/users/${userId}`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch user');
  }

  const data = await response.json();
  return data.user;
}

/**
 * Create new user (superadmin only)
 */
export async function createUser(userData: CreateUserRequest): Promise<UserData> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/api/v2/users`, {
    method: 'POST',
    headers,
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create user');
  }

  const data = await response.json();
  return data.user;
}

/**
 * Update user (superadmin only)
 */
export async function updateUser(userId: string, updates: UpdateUserRequest): Promise<UserData> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/api/v2/users/${userId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update user');
  }

  const data = await response.json();
  return data.user;
}

/**
 * Delete user (superadmin only)
 */
export async function deleteUser(userId: string): Promise<void> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/api/v2/users/${userId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete user');
  }
}


/**
 * Ensure Firebase Auth user exists and send password reset email.
 * Calls backend to create Firebase Auth account if needed, then triggers reset email client-side.
 */
export async function sendPasswordReset(userId: string): Promise<void> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/users/${userId}/reset-password`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to send password reset');
  }
}

/**
 * Generate a password reset link without sending an email.
 * Returns the link URL for manual sharing (e.g. via WhatsApp).
 */
export async function getResetLink(userId: string): Promise<string> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/users/${userId}/reset-link`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate reset link');
  }

  const data = await response.json();
  return data.link;
}

/**
 * Get all accounts (superadmin only)
 */
export async function getAllAccounts(): Promise<AccountData[]> {
  const headers = await getAuthHeaders();
  
  const response = await fetch(`${API_URL}/api/v2/accounts`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch accounts');
  }

  const data = await response.json();
  return data.accounts;
}
